// Google review link handling and local QR generation (no external QR service is ever called).
import qrcode from "qrcode-generator";

/**
 * Only accept links that open Google's "write a review" form directly:
 *   https://g.page/r/<code>/review                         (Google Business Profile → "Ask for reviews" / "Share review form")
 *   https://search.google.com/local/writereview?placeid=<id>
 * Maps share links (maps.app.goo.gl, google.com/maps/...) are rejected: they open the business listing,
 * not necessarily the review form.
 */
export function validateReviewUrl(input: string): { ok: true; url: string } | { ok: false; error: string } {
  const raw = input.trim();
  if (!raw) return { ok: true, url: "" };
  let u: URL;
  try { u = new URL(raw); } catch { return { ok: false, error: "That is not a web address. Paste the full link starting with https://" }; }
  if (u.protocol !== "https:") return { ok: false, error: "The link must start with https://" };
  const host = u.hostname.toLowerCase();
  if (host === "g.page" && /^\/r\/[A-Za-z0-9_-]{6,}\/review\/?$/.test(u.pathname)) return { ok: true, url: `https://g.page${u.pathname.replace(/\/$/, "")}` };
  if (host === "search.google.com" && u.pathname === "/local/writereview" && /^[A-Za-z0-9_-]{10,}$/.test(u.searchParams.get("placeid") ?? "")) {
    return { ok: true, url: `https://search.google.com/local/writereview?placeid=${u.searchParams.get("placeid")}` };
  }
  if (/(^|\.)maps\.app\.goo\.gl$|(^|\.)goo\.gl$|google\.[a-z.]+$/.test(host)) {
    return { ok: false, error: "This looks like a Google Maps or share link, which may not open the review form. In Google Business Profile choose “Ask for reviews” (or “Get more reviews”) and copy that link — it looks like https://g.page/r/…/review" };
  }
  return { ok: false, error: "Use the review link from your Google Business Profile — it looks like https://g.page/r/…/review" };
}

/** QR module matrix (true = dark), error correction M, smallest version that fits. */
export function qrMatrix(text: string): boolean[][] {
  const qr = qrcode(0, "M");
  qr.addData(text, "Byte");
  qr.make();
  const n = qr.getModuleCount();
  return Array.from({ length: n }, (_, r) => Array.from({ length: n }, (_, c) => qr.isDark(r, c)));
}

/** SVG path data for the dark modules, offset by a quiet zone of `margin` modules. */
export function qrSvgPath(matrix: boolean[][], margin = 4): { size: number; d: string } {
  const n = matrix.length;
  let d = "";
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (matrix[r][c]) d += `M${c + margin} ${r + margin}h1v1h-1z`;
  return { size: n + margin * 2, d };
}
